import { useQuery } from "@tanstack/react-query";
import { createLogger } from "logging";
import { SendToRequestModel } from "models/SendToRequestModel";
import { SendToSelection } from "models/SendToSelection";
import { SendToTargetModel } from "models/SendToTargetModel";
import { useApi, useMeeting, usePermissions } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import { SendToPanel } from "./components/SendToPanel";
import { SendToPanelContext } from "./SendToPanelContext";

const logger = createLogger("SendToPanelProvider");

export const SendToPanelProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const { sendToApi } = useApi();
  const [isOpen, setIsOpen] = useState(false);
  const { meetingId } = useMeeting();
  const [agendaItemId, setAgendaItemId] = useState<number | null>(null);
  const [selections, setSelections] = useState<Map<number, SendToSelection>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Query for loading send to preview
  const queryResult = useQuery({
    queryKey: [`Meeting|${meetingId}|SendToPanel|${agendaItemId}`],
    queryFn: async () => {
      if (!agendaItemId) return null;
      logger.trace("Loading send to preview...");
      const data = await sendToApi.getPreview();
      logger.trace("Loaded", data);
      return data;
    },
    enabled: isOpen && !!meetingId && !!agendaItemId,
  });

  const { error } = queryResult;
  const results = useMemo(() => queryResult.data || [], [queryResult.data]);
  const isLoading = queryResult.isPending;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  const openSendToPanel = useCallback((id: number) => {
    logger.debug("openSendToPanel()", id);
    setAgendaItemId(id);
    setIsOpen(true);
    setSelections(new Map());
    setIsSubmitting(false);
    setIsSubmitted(false);
  }, []);

  const closeSendToPanel = useCallback(() => {
    logger.debug("closeSendToPanel()");
    setIsOpen(false);
    setAgendaItemId(null);
    setSelections(new Map());
    setIsSubmitting(false);
    setIsSubmitted(false);
  }, []);

  const getSelection = useCallback(
    (targetId: number): SendToSelection => {
      return selections.get(targetId) ?? { selected: false, meetingId: null };
    },
    [selections]
  );

  const getId = useCallback((item: SendToTargetModel): number => {
    return item.target?.id ?? 0;
  }, []);

  const onSelectItem = useCallback(
    (item: SendToTargetModel) => {
      const targetId = getId(item);
      const currentSelection = selections.get(targetId);
      const newSelected = !(currentSelection?.selected ?? false);

      logger.debug("onSelectItem():", targetId, newSelected);
      setSelections((prev) => {
        const next = new Map(prev);
        next.set(targetId, {
          selected: newSelected,
          meetingId: currentSelection?.meetingId ?? null,
        });
        return next;
      });
    },
    [getId, selections]
  );

  const onSetMeeting = useCallback(
    (item: SendToTargetModel, meetingId: number) => {
      const targetId = getId(item);
      const currentSelection = selections.get(targetId);

      logger.debug("onSetMeeting():", targetId, meetingId);
      setSelections((prev) => {
        const next = new Map(prev);
        next.set(targetId, {
          selected: currentSelection?.selected ?? false,
          meetingId,
        });
        return next;
      });
    },
    [getId, selections]
  );

  const submitSendToPanel = useCallback(() => {
    if (!agendaItemId || !results || results.length === 0 || isSubmitting) {
      logger.warn("Cannot submit when items are not loaded or already submitting");
      return;
    }

    const selectedItems: SendToRequestModel[] = results
      .map((item) => {
        const targetId = getId(item);
        const selection = selections.get(targetId);
        if (!selection?.selected || !selection.meetingId || !targetId) return null;
        return { boardId: targetId, meetingId: selection.meetingId };
      })
      .filter((item): item is { boardId: number; meetingId: number } => item !== null);

    if (selectedItems.length === 0) {
      logger.warn("No items selected for submission");
      return;
    }

    setIsSubmitting(true);
    setIsSubmitted(false);

    sendToApi
      .sendTo(agendaItemId, selectedItems)
      .then(() => {
        logger.debug("Send to completed successfully");
        setIsSubmitted(true);
      })
      .catch((error: unknown) => {
        handleHttpError(error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [agendaItemId, results, getId, isSubmitting, selections, sendToApi, handleHttpError]);

  const isForm = useMemo(
    () => isOpen && !isLoading && !isSubmitting && !isSubmitted,
    [isOpen, isLoading, isSubmitting, isSubmitted]
  );

  // Compute if submit should be disabled
  const isSubmitDisabled = useMemo(() => {
    const hasSelectableItems = results.length > 0;
    const hasValidSelection = Array.from(selections.values()).some((x) => x.selected && x.meetingId && x.meetingId > 0);

    return isSubmitting || !hasSelectableItems || !hasValidSelection;
  }, [isSubmitting, results, selections]);

  const contextValue = useMemo(
    () => ({
      getId,
      isOpen,
      isLoading,
      isSubmitting,
      isSubmitted,
      isForm,
      agendaItemId,
      results,
      isSubmitDisabled,
      openSendToPanel,
      closeSendToPanel,
      getSelection,
      onSelectItem,
      onSetMeeting,
      submitSendToPanel,
    }),
    [
      getId,
      isOpen,
      isLoading,
      isSubmitting,
      isSubmitted,
      isForm,
      agendaItemId,
      results,
      isSubmitDisabled,
      openSendToPanel,
      closeSendToPanel,
      getSelection,
      onSelectItem,
      onSetMeeting,
      submitSendToPanel,
    ]
  );

  return (
    <SendToPanelContext.Provider value={contextValue}>
      {children}
      <SendToPanel />
    </SendToPanelContext.Provider>
  );
};
